import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { useElementRect } from "components/hooks/useElementRect";
import { DialogBodyContext } from "./dialogBody";

export type DialogSize = "default" | "medium" | "wide";

/// `medium` is `default`'s chrome at more width; `wide` is the full-bleed panel.
const SIZE_CLASSES: Record<DialogSize, string> = {
  default: "w-105 max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden p-6",
  medium: "w-140 max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden p-6",
  wide: "w-215 max-w-[95vw] max-h-[80vh] flex flex-col",
};

interface DialogProps {
  open: boolean;
  title?: string | React.ReactNode;
  ariaLabel?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  autoSpacing?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  size?: DialogSize;
  titleDivider?: boolean;
  blurBackdrop?: boolean;
  backdropStyle?: React.CSSProperties;
  /// Centres the surface on this element instead of on the viewport.
  anchorRef?: React.RefObject<HTMLElement | null>;
  /// Drops the surface's own box — fill, border, shadow, padding, size — and
  /// the title, body and actions slots, so the children draw their own.
  bare?: boolean;
  /// Takes focus on open in place of the surface.
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

const NO_ANCHOR: React.RefObject<HTMLElement | null> = { current: null };

export const Dialog: React.FC<DialogProps> = ({
  open,
  title,
  ariaLabel,
  children,
  actions,
  className = "",
  style,
  autoSpacing = true,
  onClose,
  showCloseButton = true,
  size = "default",
  titleDivider = true,
  blurBackdrop = false,
  backdropStyle,
  anchorRef,
  bare = false,
  initialFocusRef,
}) => {
  useEscapeStack(open, onClose);
  const anchor = useElementRect(anchorRef ?? NO_ANCHOR, open && !!anchorRef);
  const [body, setBody] = useState<HTMLDivElement | null>(null);

  const surface = useRef<HTMLDivElement>(null);

  /// Focus trap. The surface takes focus rather than the first control, which
  /// would pop a mobile keyboard on any dialog with a text field.
  useEffect(() => {
    const node = surface.current;
    if (!open || !node) return;
    const restoreTo = document.activeElement as HTMLElement | null;
    (initialFocusRef?.current ?? node).focus();

    const focusable = (): HTMLElement[] =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      restoreTo?.focus?.();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  // Anchored and still unmeasured: invisible rather than flashing at centre,
  // but focusable, since the focus effect can run before the first measure.
  const placement: React.CSSProperties | undefined = anchorRef
    ? anchor
      ? {
          position: "fixed",
          left: anchor.left + anchor.width / 2,
          top: anchor.top + anchor.height / 2,
          transform: "translate(-50%, -50%)",
        }
      : { opacity: 0 }
    : undefined;

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/20 ${
        blurBackdrop ? "backdrop-blur-sm" : ""
      }`}
      style={{ zIndex: "var(--z-dialog)", ...backdropStyle }}
      // A press on the backdrop must not pull focus out of the dialog.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      <div
        ref={surface}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={
          ariaLabel ?? (typeof title === "string" ? title : undefined)
        }
        className={
          bare
            ? `relative outline-none cursor-default ${className}`
            : `relative bg-bg-subtle border border-border rounded-app shadow-lg select-none cursor-default outline-none ${SIZE_CLASSES[size]} ${className}`
        }
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--font-size)",
          ...placement,
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {bare ? children : null}
        {!bare && title ? (
          <div
            className={`flex items-center justify-between ${
              size === "wide"
                ? `px-5 py-3 ${titleDivider ? "border-b border-border" : ""}`
                : "mb-4"
            }`}
          >
            {typeof title === "string" ? (
              <p className="text-fg-bold">{title}</p>
            ) : (
              <div className="min-w-0 flex-1">{title}</div>
            )}
            {onClose && showCloseButton && (
              <TooltipButton
                title="Close"
                aria-label="Close"
                variant="default"
                size="sm"
                onClick={onClose}
              >
                <CloseIcon />
              </TooltipButton>
            )}
          </div>
        ) : null}
        {!bare && (
          <div
            ref={setBody}
            className="relative flex flex-1 min-h-0 flex-col overflow-hidden"
          >
            <div
              className={`flex-1 min-h-0 overflow-auto ${autoSpacing ? "space-y-4" : ""}`}
            >
              <DialogBodyContext.Provider value={body}>
                {children}
              </DialogBodyContext.Provider>
            </div>
          </div>
        )}
        {!bare && actions ? (
          <div
            className={`flex shrink-0 items-center justify-end space-x-2 ${
              size === "wide" ? "px-5 py-3 border-t border-border" : "mt-6"
            }`}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
